/**
 * Maintenance Mode Configuration
 *
 * This file contains the configuration for maintenance mode.
 * When maintenance mode is enabled via NEXT_PUBLIC_MAINTENANCE_MODE,
 * only routes listed in whitelistedRoutes will be accessible.
 * All other routes will redirect to /maintenance
 */

export interface MaintenanceConfig {
  /**
   * List of route patterns that are accessible during maintenance mode
   * Supports exact paths and patterns:
   * - Exact match: "/auth/login"
   * - Pattern match: "/api/*" (matches /api/anything)
   * - Wildcard: "/public/*"
   */
  whitelistedRoutes: string[];

  /**
   * Whether to allow authenticated users access to all routes during maintenance
   * If true, logged-in users bypass maintenance mode restrictions
   */
  allowAuthenticatedUsers: boolean;
}

export const maintenanceConfig: MaintenanceConfig = {
  whitelistedRoutes: [
    // Maintenance page itself must always be accessible
    "/maintenance",
  ],

  /**
   * Set to true if authenticated users should have full access during maintenance
   * Set to false if maintenance applies to everyone regardless of auth status
   */
  allowAuthenticatedUsers: false,
};

/**
 * Check if a route matches a whitelisted pattern
 */
export function isRouteWhitelisted(
  pathname: string,
  whitelistedRoutes: string[]
): boolean {
  // Clean the pathname (remove query params and hash)
  const cleanPath = pathname.split("?")[0].split("#")[0];

  // Check for exact matches first
  if (whitelistedRoutes.includes(cleanPath)) {
    return true;
  }

  // Check for pattern matches (e.g., "/api/*")
  return whitelistedRoutes.some((pattern) => {
    if (pattern.includes("*")) {
      // Convert pattern to regex
      // Escape special regex characters except *
      const regexPattern = pattern
        .replace(/[.+?^${}()|[\]\\]/g, "\\$&")
        .replace(/\*/g, ".*");
      const regex = new RegExp(`^${regexPattern}$`);
      return regex.test(cleanPath);
    }
    return false;
  });
}

/**
 * Check if maintenance mode is enabled
 */
export function isMaintenanceModeEnabled(): boolean {
  if (typeof window === "undefined") {
    // Server-side: use process.env
    return process.env.REACT_APP_PUBLIC_MAINTENANCE_MODE === "true";
  }
  // Client-side: check the runtime config
  return process.env.REACT_APP_PUBLIC_MAINTENANCE_MODE === "true";
}
